import { useCallback, useEffect, useState } from 'react';

export type UseCountDownTimerProps = { timeInSeconds: number; expiresAt?: number };

export const formatTimeLabel = (timeInSeconds: number): string => {
  const hours = Math.floor(timeInSeconds / 3600);
  const minutes = Math.floor((timeInSeconds % 3600) / 60);
  const seconds = timeInSeconds % 60;
  const mm = minutes.toString().padStart(2, '0');
  const ss = seconds.toString().padStart(2, '0');
  // Show hours only from an hour up (the 24h login-email TTL would otherwise read "1440:00");
  // shorter timers (resend wait, phone OTP) keep the familiar MM:SS.
  return hours > 0 ? `${hours.toString().padStart(2, '0')}:${mm}:${ss}` : `${mm}:${ss}`;
};

// When `expiresAt` (ms epoch) is provided, the remaining time is derived from
// the real clock, so the countdown shows the correct value even after the
// component remounts (e.g. the modal was closed and reopened). Without it, it
// counts down the full `timeInSeconds` from mount, as before.
const computeRemaining = (timeInSeconds: number, expiresAt?: number): number =>
  expiresAt ? Math.max(0, Math.round((expiresAt - Date.now()) / 1000)) : timeInSeconds;

const toPercent = (remaining: number, timeInSeconds: number): number =>
  timeInSeconds > 0 ? (remaining / timeInSeconds) * 100 : 0;

export function useCountDownTimer({ timeInSeconds, expiresAt }: UseCountDownTimerProps) {
  const [secondsRemaining, setSecondsRemaining] = useState(() => computeRemaining(timeInSeconds, expiresAt));
  const [label, setLabel] = useState<string>(() => formatTimeLabel(computeRemaining(timeInSeconds, expiresAt)));
  const [percentElapsed, setPercentElapsed] = useState<number>(() =>
    toPercent(computeRemaining(timeInSeconds, expiresAt), timeInSeconds)
  );
  const [hasExpired, setHasExpired] = useState<boolean>(() => computeRemaining(timeInSeconds, expiresAt) <= 0);

  // Function to reset the timer
  const resetTimer = useCallback(() => {
    const remaining = computeRemaining(timeInSeconds, expiresAt);
    setSecondsRemaining(remaining);
    setLabel(formatTimeLabel(remaining));
    setPercentElapsed(toPercent(remaining, timeInSeconds));
    setHasExpired(remaining <= 0);
  }, [timeInSeconds, expiresAt]);

  // Reset the timer when timeInSeconds / expiresAt changes
  useEffect(() => resetTimer(), [resetTimer]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (secondsRemaining > 0) {
        const newTime = secondsRemaining - 1;
        setSecondsRemaining(newTime);
        setLabel(formatTimeLabel(newTime));
        setPercentElapsed(toPercent(newTime, timeInSeconds));
      } else {
        setHasExpired(true);
        clearInterval(timer);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [timeInSeconds, secondsRemaining]);

  return { label, percentElapsed, hasExpired, resetTimer };
}
